import type { ReactNode } from "react";

interface ApiStateProps {
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
  /** Shown while loading (e.g. a skeleton grid). */
  skeleton?: ReactNode;
  /** When true and there is data, the `empty` node renders instead of children. */
  isEmpty?: boolean;
  empty?: ReactNode;
  children: ReactNode;
}

/**
 * Single place for the loading → error(+retry) → empty → content flow so every
 * data-driven screen behaves the same way when the API fails.
 */
export default function ApiState({
  loading,
  error,
  onRetry,
  skeleton,
  isEmpty = false,
  empty,
  children,
}: ApiStateProps) {
  if (loading) {
    return <>{skeleton ?? <DefaultSkeleton />}</>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-700">
          <i className="ri-wifi-off-line text-2xl"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">Couldn&apos;t load this content</h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line"></i> Try again
          </button>
        )}
      </div>
    );
  }

  if (isEmpty && empty) {
    return <>{empty}</>;
  }

  return <>{children}</>;
}

function DefaultSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-32 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
      ))}
    </div>
  );
}